<template>
  <UButtonGroup
    :size="size"
    :variant="variant"
    :orientation="orientation"
    class="max-sm:!flex-col"
  >
    <template v-if="!is_mobile">
      <UButton
        icon="i-simple-icons-gmail"
        :color="color"
        :variant="variant"
        label="Gmail"
        :title="t('comps.actionEmail.btnOpen', { client: 'Gmail' })"
        @click="openEmail('gmail')"
      />
      <UButton
        icon="i-simple-icons-yahoo"
        :color="color"
        :variant="variant"
        label="Yahoo"
        :title="t('comps.actionEmail.btnOpen', { client: 'Yahoo' })"
        @click="openEmail('yahoo')"
      />
      <UButton
        icon="i-simple-icons-apple"
        :color="color"
        :variant="variant"
        label="iCloud"
        :title="t('comps.actionEmail.btnOpen', { client: 'iCloud' })"
        @click="openEmail('icloud')"
      />
      <UButton
        icon="i-mdi-outlook"
        :color="color"
        :variant="variant"
        label="Outlook"
        :title="t('comps.actionEmail.btnOpen', { client: 'Outlook' })"
        @click="openEmail('outlook')"
      />
    </template>
    <UButton
      icon="i-lucide-mail"
      :color="color"
      :variant="variant"
      :label="t('comps.actionEmail.btnOpenDefault')"
      :title="t('comps.actionEmail.btnOpen', { client: 'din klient' })"
      @click="openEmail('default')"
    />
  </UButtonGroup>
</template>

<script lang="ts" setup>
import type { ButtonSize, ButtonVariant, ButtonColor } from '#ui/types'

import type { Email } from '~~/content/types'

const is_mobile = computed(() => useIsMobile())

const props = defineProps({
  id: {
    type: String,
    required: true,
    // default: undefined,
  },
  to: {
    type: String,
    required: true,
    // default: undefined,
  },
  bcc: {
    type: String,
    required: false,
    default: undefined,
  },
  cc: {
    type: String,
    required: false,
    default: undefined,
  },
  body: {
    type: String,
    required: false,
    default: undefined,
  },
  subject: {
    type: String,
    required: false,
    default: undefined,
  },
  targetName: {
    type: String,
    required: false,
    default: undefined,
  },
  customReplace: {
    type: String,
    required: false,
    default: undefined,
  },
  emailTemplates: {
    type: Array as PropType<Email[]>,
    required: false,
    default: () => [],
  },
  // name: {
  //   type: String,
  //   required: true,
  //   default: undefined,
  // },
  color: {
    type: String as PropType<ButtonColor>,
    required: false,
    default: 'neutral',
  },
  variant: {
    type: String as PropType<ButtonVariant>,
    required: false,
    default: 'subtle',
  },
  orientation: {
    type: String as PropType<'horizontal' | 'vertical'>,
    required: false,
    default: 'vertical',
  },
  size: {
    type: String as PropType<ButtonSize>,
    required: false,
    default: 'sm',
  },
})

// TODO: consider hiding buttons not relevant for the user email
// <!-- v-if="person.email.includes('@gmail.com')"
// v-else-if="person.email.includes('@yahoo')" -->

// TODO: Add an email service that registrates people sending emails to it. It should send an callback when an email is recived.
// sendgrid.activoice.online

const emit = defineEmits(['linkClicked'])

const user = useUserStore()

function openEmail(service: 'gmail' | 'outlook' | 'yahoo' | 'icloud' | 'default') {
  let link: string

  if (props.bcc && props.bcc.length >= 254) console.error('Mailto: BCC is too long')

  const to = encodeURIComponent(props.to || '')
  const bcc = encodeURIComponent(props.bcc || '')
  const cc = encodeURIComponent(props.cc || '')
  let subject = ''
  let body = ''

  if (props.emailTemplates.length > 0) {
    const template = props.emailTemplates[Math.floor(Math.random() * props.emailTemplates.length)]
    if (!template) {
      console.error('Mailto: No email template found')
      return
    }
    subject = template.subject
    body = template.body
  }
  else {
    subject = props.subject || ''
    body = props.body || ''
  }

  subject = encodeURIComponent(addInfo(subject, user, props.targetName, props.customReplace))
  body = encodeURIComponent(addInfo(body, user, props.targetName, props.customReplace))

  if (service === 'gmail') {
    link = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&bcc=${bcc}&cc=${cc}&su=${subject}&body=${body}`
  }
  else if (service === 'outlook') {
    link = `https://outlook.live.com/mail/0/deeplink/compose?to=${to}&bcc=${bcc}&cc=${cc}&subject=${subject}&body=${body}`
  }
  else if (service === 'yahoo') {
    link = `https://compose.mail.yahoo.com/?to=${to}&bcc=${bcc}&cc=${cc}&subject=${subject}&body=${body}`
  }
  else if (service === 'icloud') {
    link = `https://www.icloud.com/mail#compose?to=${to}&bcc=${bcc}&cc=${cc}&subject=${subject}&body=${body}`
  }
  else if (service === 'default') {
    link = `mailto:${to}?bcc=${bcc}&cc=${cc}&subject=${subject}&body=${body}`
  }
  else {
    console.error('Mailto: Unknown service')
    return
  }

  navigateTo(link, {
    external: true,
    open: {
      target: '_blank',
    },
  })

  emit('linkClicked', { id: props.id })
}
</script>
